import { useEffect } from "react";
import type { ReviewProgressItem } from "@compliance-sage/contracts";
import { LiveReviewGraph } from "../features/settings/sageWorkflow/LiveReviewGraph";
import { ReviewProgressDetail } from "./ReviewProgressDetail";
import { Card } from "./ui";
import { useI18n } from "../i18n";

/**
 * A docked, full-height live-workflow view — not a modal. It never blocks the rest of
 * the app (no backdrop, no focus trap, no scroll lock) since it's meant to stay open as
 * ambient status while the user keeps working, the same way an IDE's side panel would.
 */
export function ReviewProgressDrawer({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: ReviewProgressItem[];
}) {
  const { t } = useI18n();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <aside
      aria-label={t("analysisProgressTitle")}
      aria-hidden={!open}
      className={`fixed inset-y-0 right-0 z-50 flex h-screen w-full max-w-5xl flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ${
        open ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <h2 className="text-base font-bold text-slate-800">{t("analysisProgressTitle")}</h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-md text-lg text-slate-500 transition hover:bg-slate-100"
          aria-label={t("sysGuideClose")}
          title={t("sysGuideClose")}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {!open ? null : items.length === 0 ? (
          <Card>
            <p className="text-sm text-slate-500">{t("sageWorkflowNoActiveReviews")}</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <Card key={item.reviewId} className="space-y-4">
                <LiveReviewGraph item={item} />
                <div className="border-t border-slate-100 pt-4">
                  <ReviewProgressDetail item={item} showOpenLink sourcesExpandedByDefault />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
